<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn class="mx-2" fab x-small color="primary" @click="$router.back()">
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          Edit Expense
          <v-chip
            v-if="form.expense_number"
            small
            class="ml-3"
            color="grey lighten-2"
          >
            {{ form.expense_number }}
          </v-chip>
        </v-card-title>

        <v-card-text v-if="isFetching">
          <v-progress-linear indeterminate color="primary"></v-progress-linear>
        </v-card-text>

        <v-card-text v-else>
          <!-- Classification -->
          <h4 class="text-subtitle-1 font-weight-bold">Classification</h4>
          <v-divider class="mb-4 mt-1"></v-divider>
          <v-row>
            <v-col sm="6">
              <v-select
                v-model="expenseType"
                :items="typeOptions"
                outlined
                label="Expense Type"
                prepend-inner-icon="mdi-shape-outline"
                @change="form.expense_category_id = null"
              ></v-select>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.expense_category_id"
                :items="filteredCategories"
                item-text="name"
                item-value="id"
                outlined
                label="Category"
                :disabled="expenseType === null"
                prepend-inner-icon="mdi-tag-outline"
                :error-messages="errors.expense_category_id"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <!-- Details -->
          <h4 class="text-subtitle-1 font-weight-bold mt-6">Details</h4>
          <v-divider class="mb-4 mt-1"></v-divider>
          <v-row>
            <v-col sm="12">
              <v-text-field
                v-model="form.title"
                outlined
                label="Title / Description"
                :error-messages="errors.title"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.amount"
                outlined
                type="number"
                min="0"
                step="0.01"
                prefix="₹"
                label="Amount"
                :error-messages="errors.amount"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.expense_date"
                outlined
                type="date"
                label="Expense Date"
                :error-messages="errors.expense_date"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Payment -->
          <h4 class="text-subtitle-1 font-weight-bold mt-6">Payment</h4>
          <v-divider class="mb-4 mt-1"></v-divider>
          <v-row>
            <v-col sm="6">
              <v-select
                v-model="form.payment_mode"
                :items="paymentModes"
                outlined
                label="Payment Mode"
                prepend-inner-icon="mdi-cash"
                :error-messages="errors.payment_mode"
              ></v-select>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.reference_number"
                outlined
                label="Reference Number"
                :error-messages="errors.reference_number"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Attachment -->
          <h4 class="text-subtitle-1 font-weight-bold mt-6">Bill / Receipt</h4>
          <v-divider class="mb-4 mt-1"></v-divider>
          <v-row>
            <v-col sm="12" v-if="existingAttachment">
              <v-btn
                text
                small
                color="primary"
                :href="mediaUrl + existingAttachment"
                target="_blank"
              >
                <v-icon left small>mdi-paperclip</v-icon>
                View current attachment
              </v-btn>
            </v-col>
            <v-col sm="12">
              <v-file-input
                v-model="attachment"
                outlined
                :label="
                  existingAttachment
                    ? 'Replace Bill / Receipt'
                    : 'Attach Bill / Receipt'
                "
                accept="image/jpeg,image/jpg,image/png,application/pdf"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                hint="JPG, PNG or PDF. Max 5 MB."
                persistent-hint
                show-size
                clearable
                :error-messages="errors.attachment"
              ></v-file-input>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                rows="2"
                auto-grow
                :error-messages="errors.remarks"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Audit -->
          <h4 class="text-subtitle-1 font-weight-bold mt-6">Audit</h4>
          <v-divider class="mb-4 mt-1"></v-divider>
          <v-row>
            <v-col sm="6">
              <div class="text-caption grey--text">Created By</div>
              <div>{{ audit.created_by || "-" }}</div>
            </v-col>
            <v-col sm="6">
              <div class="text-caption grey--text">Created At</div>
              <div>{{ $options.filters.niceDate(audit.created_at) || "-" }}</div>
            </v-col>
          </v-row>

          <v-divider class="mt-5 mb-5"></v-divider>
          <div class="text-right">
            <v-btn text class="mr-2" @click="$router.back()">Cancel</v-btn>
            <v-btn
              :loading="isLoading"
              :disabled="isLoading"
              rounded
              color="primary"
              dark
              @click="save"
            >
              Update
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Error -->
    <v-dialog v-model="errorDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: red; font-size: 90px">
                mdi-alert-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: red">Error!</h1>
                <br />
                <h3>{{ errorMessage }}</h3>
                <br />
                <v-btn color="error" @click="errorDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "ExpenseIdPage",
  data() {
    return {
      form: {
        expense_number: null,
        expense_category_id: null,
        title: "",
        amount: null,
        expense_date: null,
        payment_mode: 0,
        reference_number: null,
        remarks: null,
      },
      expenseType: null,
      attachment: null,
      existingAttachment: null,
      audit: {},
      categories: [],
      isFetching: true,
      isLoading: false,
      errorDialog: false,
      errorMessage: "",
      typeOptions: [
        { text: "Direct", value: 0 },
        { text: "Indirect", value: 1 },
      ],
      paymentModes: [
        { text: "Cash", value: 0 },
        { text: "Bank Transfer", value: 1 },
        { text: "Cheque", value: 2 },
        { text: "UPI", value: 3 },
        { text: "Other", value: 4 },
      ],
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    filteredCategories() {
      if (this.expenseType === null) return [];
      return this.categories.filter((c) => c.type === this.expenseType);
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.getMasters();
    this.getData();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get("/masters", {
          params: { context: "expense" },
        });
        this.categories = response.data.expense_categories || [];
      } catch (error) {
        console.log(error);
      }
    },

    async getData() {
      this.isFetching = true;
      try {
        const response = await axios.get(`/expenses/${this.$route.params.id}`);
        const expense = response.data.data;

        this.form = {
          expense_number: expense.expense_number,
          expense_category_id: expense.expense_category_id,
          title: expense.title,
          amount: expense.amount,
          expense_date: expense.expense_date
            ? String(expense.expense_date).slice(0, 10)
            : null,
          payment_mode: expense.payment_mode,
          reference_number: expense.reference_number,
          remarks: expense.remarks,
        };
        this.expenseType = expense.expense_type;
        this.existingAttachment = expense.attachment_path;

        this.audit = {
          created_by: expense.created_by
            ? `${expense.created_by.first_name || ""} ${
                expense.created_by.last_name || ""
              }`.trim()
            : null,
          created_at: expense.created_at
            ? String(expense.created_at).slice(0, 10)
            : null,
        };
      } catch (error) {
        console.log(error);
        this.errorMessage = "Failed to load this expense.";
        this.errorDialog = true;
      }
      this.isFetching = false;
    },

    async save() {
      this.isLoading = true;
      this.$store.dispatch("errors/setErrors", {});
      try {
        if (this.attachment) {
          // Multipart has to go over POST - PATCH won't carry the file.
          const data = new FormData();
          Object.keys(this.form).forEach((key) => {
            if (key === "expense_number") return;
            const value = this.form[key];
            if (value !== null && value !== undefined && value !== "") {
              data.append(key, value);
            }
          });
          data.append("attachment", this.attachment);
          await axios.post(`/expenses/${this.$route.params.id}`, data, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          const payload = { ...this.form };
          delete payload.expense_number;
          await axios.patch(`/expenses/${this.$route.params.id}`, payload);
        }

        this.$router.push("/expenses");
      } catch (error) {
        console.log(error);
        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            this.$store.dispatch("errors/setErrors", error.response.data.errors);
          } else {
            this.errorMessage =
              error.response.data.message || "Failed to update expense";
            this.errorDialog = true;
          }
        }
      }
      this.isLoading = false;
    },
  },
};
</script>
